import Phaser from "phaser";
import { sfx, unlockAudio } from "@shared/audio";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { BG, FONT, PANEL, PANEL_2, TEXT_DIM, W, backdrop, button, centerCamera, confetti, floatText, overlay, share, text } from "@kit/ui";
import { currentLang, pick, publicGameUrl } from "@kit/lang";
import { afterSkip, afterWin, mapBackLink, mapText, openMap } from "@kit/map";
import { recordSkip, recordWin } from "@kit/progress";
import { track } from "@kit/telemetry";
import { noKnowledgeMoves, perfectMemoryMoves } from "./core/bot";
import { closeOpenCards, createGame, selectCard, starTarget, starsFor, type Card, type GameState } from "./core/game";
import { generateLevel } from "./core/generator";
import { t } from "./i18n";

const ACCENT = 0x1982c4;
const PAPER = 0xf7f3e9;
const INK = "#182943";
const GRID_TOP = 330;
const GRID_GAP = 14;

type Save = {
  level: number;
  restartsByLevel: Record<string, number>;
  bestByLevel: Record<string, { moves: number; stars: number }>;
};
type CardView = {
  container: Phaser.GameObjects.Container;
  back: Phaser.GameObjects.Container;
  front: Phaser.GameObjects.Container;
  glow: Phaser.GameObjects.Rectangle;
};

export class BaskentScene extends Phaser.Scene {
  private lang = currentLang();
  private save!: Save;
  private state!: GameState;
  private views: CardView[] = [];
  private centers: { x: number; y: number }[] = [];
  private cardWidth = 150;
  private cardHeight = 140;
  /** Bottom of the card area: the buttons sit under it, at the bottom of tall phones. */
  private gridBottom = 1080;
  private moveLabel!: Phaser.GameObjects.Text;
  private matchLabel!: Phaser.GameObjects.Text;
  private levelValue!: Phaser.GameObjects.Text;
  private restartButton!: Phaser.GameObjects.Container;
  private shareButton!: Phaser.GameObjects.Container;
  private skipButton!: Phaser.GameObjects.Container;
  private skipVisible = false;
  private mismatchTimer?: number;
  private winOverlay?: Phaser.GameObjects.Container;
  private levelStartedAt = 0;
  private autoplayBusy = false;
  private levelArg?: number;

  constructor() {
    super("baskent");
  }

  init(data?: { level?: number }): void {
    // The scene is re-entered from the level map: drop views of the previous visit.
    this.levelArg = data?.level;
    this.views = [];
    this.centers = [];
    this.winOverlay = undefined;
    this.autoplayBusy = false;
    this.skipVisible = false;
  }

  create(): void {
    centerCamera(this);
    this.cameras.main.setBackgroundColor(BG);
    backdrop(this, { light: 0x1982c4 });
    this.drawAtlasBackdrop();
    this.save = loadSave<Save>("baskent", { level: 1, restartsByLevel: {}, bestByLevel: {} });
    this.save.restartsByLevel ??= {};
    this.save.bestByLevel ??= {};
    const queryLevel = Number(new URLSearchParams(location.search).get("level"));
    const initialLevel = this.levelArg ?? (Number.isInteger(queryLevel) && queryLevel > 0 ? queryLevel : Math.max(1, this.save.level || 1));

    this.add.rectangle(W / 2, 0, W * 3, 150, 0x111d38).setOrigin(0.5, 0);
    this.add.text(34, 43, t(this.lang, "name"), { fontFamily: FONT, fontSize: "31px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0, 0.5);
    mapBackLink(this, "baskent", 36, 99, "#9db5d0", 20);
    this.add.text(W - 34, 29, t(this.lang, "level"), { fontFamily: FONT, fontSize: "17px", color: "#92a9c5", fontStyle: "bold" }).setOrigin(1, 0.5);
    this.levelValue = this.add.text(W - 34, 86, "1", { fontFamily: FONT, fontSize: "50px", color: "#ffffff", fontStyle: "bold" }).setOrigin(1, 0.5);
    this.moveLabel = text(this, W / 2, 207, "", 25, "#ffffff");
    this.add.text(W / 2, 256, t(this.lang, "instruction"), { fontFamily: FONT, fontSize: "20px", color: "#9fb4ce" }).setOrigin(0.5);
    this.matchLabel = text(this, W / 2, 296, "", 22, "#70d4cb", false).setWordWrapWidth(650);

    const buttonsY = Math.max(1150, this.scale.height - 170);
    this.gridBottom = buttonsY - 70;
    this.restartButton = button(this, 246, buttonsY, 306, 76, t(this.lang, "restart"), ACCENT, () => this.restartLevel());
    this.shareButton = button(this, 520, buttonsY, 220, 76, t(this.lang, "share"), PANEL_2, () => void this.shareScore());
    this.skipButton = button(this, W / 2, buttonsY + 80, 240, 62, t(this.lang, "skip"), 0x38516f, () => this.skipLevel());
    this.skipButton.setVisible(false);
    this.input.keyboard?.on("keydown-R", this.restartLevel, this);
    this.events.once("shutdown", () => {
      this.clearMismatchTimer();
      this.input.keyboard?.off("keydown-R", this.restartLevel, this);
    });

    this.startLevel(initialLevel, this.save.restartsByLevel[String(initialLevel)] ?? 0);
    exposeBotHook({
      state: () => ({
        ...this.state,
        screen: {
          cards: this.centers.map((point) => this.screenPoint(point.x, point.y)),
          restart: this.screenPoint(this.restartButton.x, this.restartButton.y),
          share: this.screenPoint(this.shareButton.x, this.shareButton.y),
          ...(this.skipVisible ? { skip: this.screenPoint(this.skipButton.x, this.skipButton.y) } : {}),
        },
      }),
      autoplay: () => this.autoplay(),
      share: () => this.shareScore(),
    });
  }

  private drawAtlasBackdrop(): void {
    const g = this.add.graphics().setDepth(-5);
    g.lineStyle(2, 0x2c4d71, 0.2);
    g.beginPath();
    g.moveTo(-30, 440);
    g.lineTo(190, 365);
    g.lineTo(360, 450);
    g.lineTo(555, 375);
    g.lineTo(760, 455);
    g.strokePath();
    g.beginPath();
    g.moveTo(-20, 1000);
    g.lineTo(175, 1060);
    g.lineTo(380, 985);
    g.lineTo(575, 1050);
    g.lineTo(750, 970);
    g.strokePath();
    for (const [x, y] of [[95, 410], [192, 366], [358, 449], [554, 376], [636, 420], [175, 1060], [381, 986], [575, 1050]] as const) {
      g.fillStyle(ACCENT, 0.25).fillCircle(x, y, 5);
      g.lineStyle(1, 0x74a3ca, 0.15).strokeCircle(x, y, 12);
    }
    for (let i = 0; i < 7; i++) {
      this.add.circle(42 + i * 104, 320 + (i % 2) * 28, 2, 0x83a3c2, 0.25).setDepth(-4);
    }
  }

  private clearMismatchTimer(): void {
    if (this.mismatchTimer !== undefined) window.clearTimeout(this.mismatchTimer);
    this.mismatchTimer = undefined;
  }

  private startLevel(level: number, restarts: number): void {
    this.clearMismatchTimer();
    this.winOverlay?.destroy();
    this.winOverlay = undefined;
    this.views.forEach((view) => view.container.destroy());
    this.views = [];
    this.state = createGame(level, restarts);
    this.levelStartedAt = Date.now();
    this.matchLabel.setText("");
    this.save.level = level;
    this.writeProgress();
    this.makeCards();
    this.refresh();
    track("level_start", { level });
  }

  private makeCards(): void {
    const deal = generateLevel(this.state.level);
    const cols = deal.cols;
    const rows = deal.rows;
    this.cardWidth = Math.min(205, Math.floor((W - 48 - (cols - 1) * GRID_GAP) / cols));
    this.cardHeight = Math.min(236, Math.floor((this.gridBottom - GRID_TOP - (rows - 1) * GRID_GAP) / rows));
    const totalW = cols * this.cardWidth + (cols - 1) * GRID_GAP;
    const totalH = rows * this.cardHeight + (rows - 1) * GRID_GAP;
    const left = (W - totalW) / 2;
    const top = (GRID_TOP + this.gridBottom) / 2 - totalH / 2;
    this.centers = this.state.cards.map((_, index) => ({
      x: left + this.cardWidth / 2 + (index % cols) * (this.cardWidth + GRID_GAP),
      y: top + this.cardHeight / 2 + Math.floor(index / cols) * (this.cardHeight + GRID_GAP),
    }));
    this.state.cards.forEach((card, index) => this.views.push(this.makeCard(card, index, this.centers[index]!)));
  }

  private makeCard(card: Card, index: number, position: { x: number; y: number }): CardView {
    const w = this.cardWidth;
    const h = this.cardHeight;
    const back = this.add.container(0, 0, [this.add.image(0, 0, this.cardBackTexture(w, h))]);

    const front = this.add.container(0, 0);
    const faceBg = this.add.graphics();
    faceBg.fillStyle(PAPER, 1).fillRoundedRect(-w / 2, -h / 2, w, h, 20);
    faceBg.lineStyle(3, 0x97afc1, 0.9).strokeRoundedRect(-w / 2, -h / 2, w, h, 20);
    front.add(faceBg);
    const cardData = this.state.cards[index]!;
    const pair = this.state.pairs.find((candidate) => candidate.id === cardData.pairId)!;
    const isCountry = cardData.kind === "country";
    const typeText = this.add.text(0, -h / 2 + Math.min(19, h * 0.17), isCountry ? t(this.lang, "country") : t(this.lang, "capital"), {
      fontFamily: FONT, fontSize: `${Math.max(10, Math.min(15, h * 0.105))}px`, color: "#62778a", fontStyle: "bold", letterSpacing: 1,
    }).setOrigin(0.5);
    front.add(typeText);

    if (isCountry) {
      const bandW = Math.min(84, w * 0.58);
      const bandH = Math.max(16, Math.min(27, h * 0.19));
      const stripe = this.add.graphics();
      const colors = pair.country.colors;
      const lineH = bandH / colors.length;
      colors.forEach((color, stripeIndex) => {
        const shift = stripeIndex % 2 === 0 ? -2 : 2;
        stripe.fillStyle(color, 1).fillRoundedRect(-bandW / 2 + shift, -h / 2 + h * 0.34 + stripeIndex * lineH, bandW - Math.abs(shift), Math.max(4, lineH + 1), 4);
      });
      // Tiny uneven edge marks make the colour ribbon feel hand-drawn, not like a flag replica.
      stripe.lineStyle(1, 0x536277, 0.34).lineBetween(-bandW / 2 + 3, -h / 2 + h * 0.34 - 2, bandW / 2 - 1, -h / 2 + h * 0.34 - 1);
      front.add(stripe);
    } else {
      const city = this.add.graphics();
      const gy = -h / 2 + h * 0.39;
      city.fillStyle(0x79a9bd, 1).fillCircle(0, gy - 13, 8);
      city.fillStyle(0x1982c4, 1).fillRoundedRect(-30, gy - 2, 19, 30, 3).fillRoundedRect(-8, gy - 14, 20, 42, 3).fillRoundedRect(15, gy + 4, 17, 24, 3);
      city.fillStyle(0xe8f0ef, 0.95);
      for (const [x, y] of [[-25, gy + 5], [-17, gy + 5], [-3, gy - 5], [5, gy - 5], [20, gy + 10], [27, gy + 10]] as const) city.fillRect(x, y, 3, 5);
      city.lineStyle(3, 0x1982c4, 1).lineBetween(-36, gy + 29, 37, gy + 29);
      front.add(city);
    }
    const name = isCountry ? pick(this.lang, { tr: pair.country.tr, en: pair.country.en }) : pick(this.lang, { tr: pair.country.capitalTr, en: pair.country.capitalEn });
    const fontSize = name.length > 22 ? 12 : name.length > 17 ? 14 : name.length > 13 ? 16 : name.length > 9 ? 18 : 21;
    const labelY = isCountry ? h * 0.23 : h * 0.25;
    const label = this.add.text(0, labelY, name, {
      fontFamily: FONT, fontSize: `${Math.min(fontSize, Math.max(12, h * 0.15))}px`, color: INK, fontStyle: "bold", align: "center",
      wordWrap: { width: w - 20, useAdvancedWrap: true }, lineSpacing: -2,
    }).setOrigin(0.5).setWordWrapWidth(w - 20);
    front.add(label);
    const glow = this.add.rectangle(0, 0, w - 4, h - 4, 0x4ed6b1, 0).setAlpha(0);
    const container = this.add.container(position.x, position.y, [back, front, glow]).setSize(w, h).setDepth(10).setInteractive({ useHandCursor: true });
    front.setVisible(false);
    container.on("pointerdown", (_pointer: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      this.tapCard(index);
    });
    return { container, back, front, glow };
  }

  /** Atlas-style card back (blue paper, a globe with meridians), baked once per card size. */
  private cardBackTexture(w: number, h: number): string {
    const key = `baskent-back-${w}x${h}`;
    if (this.textures.exists(key)) return key;
    const tex = this.textures.createCanvas(key, w, h);
    if (!tex) return key;
    const ctx = tex.getContext();
    const rr = (x: number, y: number, rw: number, rh: number, r: number) => {
      ctx.beginPath();
      if (typeof ctx.roundRect === "function") ctx.roundRect(x, y, rw, rh, r);
      else ctx.rect(x, y, rw, rh);
    };
    const paper = ctx.createLinearGradient(0, 0, w, h);
    paper.addColorStop(0, "#2f6fa3");
    paper.addColorStop(1, "#173a63");
    ctx.fillStyle = paper;
    rr(0, 0, w, h, 20);
    ctx.fill();
    ctx.strokeStyle = "rgba(190,225,255,0.45)";
    ctx.lineWidth = 2;
    rr(7, 7, w - 14, h - 14, 14);
    ctx.stroke();
    const r = Math.min(w, h) * 0.27;
    const cx = w / 2;
    const cy = h / 2;
    const globe = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r);
    globe.addColorStop(0, "#7fd6f2");
    globe.addColorStop(1, "#2a86c4");
    ctx.fillStyle = globe;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = 1.5;
    for (const k of [-0.5, 0, 0.5]) {
      ctx.beginPath();
      ctx.ellipse(cx, cy, r * Math.abs(k === 0 ? 0.02 : 0.55), r, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - r * Math.sqrt(1 - k * k), cy + k * r);
      ctx.lineTo(cx + r * Math.sqrt(1 - k * k), cy + k * r);
      ctx.stroke();
    }
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.10)";
    for (let i = 0; i < 18; i++) {
      ctx.beginPath();
      ctx.arc(((i * 53) % (w - 24)) + 12, ((i * 37) % (h - 24)) + 12, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
    tex.refresh();
    return key;
  }

  private tapCard(index: number, fromBot = false): void {
    if ((this.autoplayBusy && !fromBot) || this.state.phase !== "playing") return;
    const tapped = this.state.cards[index];
    if (!tapped || tapped.matched || tapped.faceUp) return;
    const oldMoves = this.state.moves;
    const beforeMatched = this.state.matchedPairs;
    unlockAudio();
    const next = selectCard(this.state, index);
    if (next === this.state) return;
    this.clearMismatchTimer();
    this.state = next;
    const newCard = this.views[index]?.container;
    if (newCard) {
      newCard.setScale(0.94, 1);
      this.tweens.add({ targets: newCard, scaleX: 1, duration: 150, ease: "Back.Out" });
    }
    if (this.state.moves > oldMoves) {
      track("move", { level: this.state.level, moves: this.state.moves, matched: this.state.matchedPairs > beforeMatched });
      if (this.state.matchedPairs > beforeMatched) {
        sfx.pop(3);
        const pair = this.state.pairs.find((candidate) => candidate.id === this.state.cards[index]!.pairId)!;
        const label = `${pick(this.lang, { tr: pair.country.capitalTr, en: pair.country.capitalEn })} ↔ ${pick(this.lang, { tr: pair.country.tr, en: pair.country.en })}`;
        this.matchLabel.setText(label);
        floatText(this, this.centers[index]!.x, this.centers[index]!.y, label, "#91f0d5", 22);
        for (const card of this.state.cards) if (card.pairId === pair.id) {
          const view = this.views[card.index]!;
          this.tweens.add({ targets: view.container, scale: 1.045, duration: 125, yoyo: true, ease: "Back.Out" });
          view.glow.setAlpha(0.36);
          this.tweens.add({ targets: view.glow, alpha: 0.12, duration: 520, yoyo: true, repeat: 1 });
        }
      } else {
        sfx.nope();
      }
    } else {
      sfx.goalTick();
    }
    this.refresh();
    if (this.state.open.length === 2) {
      // Do not lock input: a new card tap closes this pair immediately and starts another.
      this.mismatchTimer = window.setTimeout(() => {
        this.mismatchTimer = undefined;
        if (this.state.open.length === 2 && this.state.phase === "playing") {
          this.state = closeOpenCards(this.state);
          this.refresh();
        }
      }, 700);
    }
    if (this.state.phase === "won") this.finishLevel();
  }

  private refresh(): void {
    const target = starTarget(this.state.pairCount);
    this.moveLabel.setText(`${this.lang === "tr" ? "Hamle" : "Moves"} ${this.state.moves} · ★★★ ${t(this.lang, "target")} ≤ ${target}`);
    this.levelValue.setText(String(this.state.level));
    this.views.forEach((view, index) => {
      const card = this.state.cards[index]!;
      const visible = card.faceUp || card.matched;
      view.back.setVisible(!visible);
      view.front.setVisible(visible);
      view.glow.setAlpha(card.matched ? 0.16 : 0);
      view.container.setAlpha(card.matched ? 1 : 0.98);
    });
    this.skipVisible = this.state.restarts >= 3;
    this.skipButton.setVisible(this.skipVisible);
    this.skipButton.setAlpha(this.skipVisible ? 1 : 0);
  }

  private finishLevel(): void {
    this.clearMismatchTimer();
    const stars = starsFor(this.state.moves, this.state.pairCount);
    const oldBest = this.save.bestByLevel[String(this.state.level)];
    if (!oldBest || stars > oldBest.stars || (stars === oldBest.stars && this.state.moves < oldBest.moves)) {
      this.save.bestByLevel[String(this.state.level)] = { moves: this.state.moves, stars };
    }
    this.writeProgress();
    const level = this.state.level;
    const outcome = recordWin("baskent", level, stars);
    track("level_end", { level, result: "won", moves: this.state.moves, secs: Math.floor((Date.now() - this.levelStartedAt) / 1000), stars });
    sfx.win();
    confetti(this);
    const starLine = `${"★".repeat(stars)}${"☆".repeat(3 - stars)}`;
    const sub = t(this.lang, "winSubtitle").replace("{moves}", String(this.state.moves)).replace("{stars}", starLine);
    this.winOverlay = overlay(this, t(this.lang, "winTitle"), sub, [
      { label: mapText("continue", this.lang), color: ACCENT, onClick: () => this.leaveLevel(afterWin(level, outcome)) },
      { label: t(this.lang, "share"), color: PANEL_2, onClick: () => void this.shareScore() },
    ]);
  }

  private restartLevel(): void {
    if (this.state.phase === "won") return;
    unlockAudio();
    sfx.shuffle();
    const restarts = this.state.restarts + 1;
    this.save.restartsByLevel[String(this.state.level)] = restarts;
    this.startLevel(this.state.level, restarts);
  }

  private skipLevel(): void {
    if (!this.skipVisible || this.state.phase !== "playing") return;
    track("level_end", { level: this.state.level, result: "skip", moves: this.state.moves, secs: Math.floor((Date.now() - this.levelStartedAt) / 1000) });
    sfx.special();
    this.leaveLevel(afterSkip(this.state.level, recordSkip("baskent", this.state.level)));
  }

  private leaveLevel(back: Parameters<typeof openMap>[2]): void {
    const nextLevel = this.state.level + 1;
    delete this.save.restartsByLevel[String(nextLevel)];
    this.save.level = Math.max(this.save.level, nextLevel);
    this.writeProgress();
    openMap(this, "baskent", back);
  }

  private writeProgress(): void {
    writeSave("baskent", this.save);
  }

  private async shareScore(): Promise<boolean> {
    track("share_click", { level: this.state.level, moves: this.state.moves });
    const query = new URLSearchParams({ level: String(this.state.level) });
    const result = await share(
      t(this.lang, "shareScore").replace("{moves}", String(this.state.moves)),
      `${publicGameUrl("baskent")}?${query.toString()}`,
      t(this.lang, "shareTitle"),
    );
    if (result) track("share_done", { level: this.state.level });
    return result;
  }

  private async autoplay(): Promise<void> {
    if (this.state.phase === "won" || this.autoplayBusy) return;
    this.autoplayBusy = true;
    this.clearMismatchTimer();
    for (const index of perfectMemoryMoves(this.state.level)) {
      this.tapCard(index, true);
      await new Promise<void>((resolve) => this.time.delayedCall(18, resolve));
    }
    this.autoplayBusy = false;
  }

  private screenPoint(x: number, y: number): { x: number; y: number } {
    const camera = this.cameras.main;
    const canvas = this.game.canvas;
    const cssScale = canvas.getBoundingClientRect().width / this.scale.width;
    return { x: (x - camera.scrollX) * camera.zoom * cssScale, y: (y - camera.scrollY) * camera.zoom * cssScale };
  }
}
